import { useDebounce } from "@flatfile/design-system";
import { matchSorter } from "match-sorter";
import { useRef, useState, useEffect } from "react";
import { FetchStatus } from "../../../utils/buffer/types.js";
const PAGE_SIZE = 100;
const PAGE_LIMIT = 1;
const DEFAULT_KEYS = ["label", "value"];
const MAX_OPTIONS = PAGE_SIZE * PAGE_LIMIT;
const useLookupOptions = ({
  options,
  keys,
  keyPressValue,
  refetchOnChange,
  rowData
}) => {
  const isMounted = useRef(false);
  const isFunctionType = typeof options === "function";
  const filterOptions = (opts, search) => {
    return matchSorter(opts, search, {
      keys: keys ?? DEFAULT_KEYS,
      baseSort: (a, b) => a.index < b.index ? -1 : 1
    });
  };
  const initOptions = Array.isArray(options) ? options : [];
  const initStatus = isFunctionType ? FetchStatus.loading : FetchStatus.success;
  const [fetchOnChange, setFetchOnChange] = useState(
    refetchOnChange || isFunctionType
  );
  const [fetchStatus, setFetchStatus] = useState(initStatus);
  const [inputValue, setInputValue] = useState(keyPressValue ?? "");
  const [optionsList, setOptionsList] = useState(initOptions);
  const [filteredOptionsList, setFilteredOptionsList] = useState(filterOptions(initOptions, inputValue));
  const debouncedSearchTerm = useDebounce(inputValue, 350);
  const fetchInitOptions = async (searchTerm) => {
    if (!isFunctionType)
      return;
    if (isMounted.current) {
      setFetchStatus(FetchStatus.loading);
    }
    try {
      const fetchedOptions = await options(searchTerm, 1, PAGE_SIZE, rowData);
      if (isMounted.current) {
        if (!searchTerm && fetchedOptions.length < PAGE_SIZE) {
          setFetchOnChange(false);
        }
        setOptionsList(fetchedOptions);
        setFilteredOptionsList(fetchedOptions);
        setFetchStatus(FetchStatus.success);
      }
    } catch {
      if (isMounted.current) {
        setFetchStatus(FetchStatus.failed);
      }
    }
  };
  const resetOptions = () => {
    setInputValue("");
  };
  const handleInputChange = (event) => {
    var _a;
    setInputValue(
      typeof event === "string" ? event : ((_a = event.currentTarget) == null ? void 0 : _a.value) ?? ""
    );
  };
  useEffect(() => {
    isMounted.current = true;
    fetchInitOptions();
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (isFunctionType && fetchOnChange) {
      fetchInitOptions(debouncedSearchTerm);
    } else {
      setFilteredOptionsList(filterOptions(optionsList, debouncedSearchTerm));
    }
  }, [debouncedSearchTerm]);
  return {
    fetchStatus,
    filteredOptionsList,
    resetOptions,
    isFunctionType,
    inputValue,
    onInputChange: handleInputChange
  };
};
export {
  MAX_OPTIONS,
  useLookupOptions
};
